/**
 * Spec Phase 7 Slice 7D + 7E — AutoGeneratedSection.
 *
 * Renders the live HTML payload for the two auto-generated spec
 * sections (reference_signs_list, claims_mirror). The payload is
 * fetched + cached by the spec store via getSpecSectionRender;
 * this component only handles presentation:
 *
 *   - banner ("Auto-generated · Refresh") at the top of the paper
 *     surface, with a refresh button that force-reloads the render
 *   - one rendered_paragraphs entry per visual block
 *     (numerals → table; claims → per-claim paragraphs)
 *   - inline status states: loading skeleton, error retry,
 *     empty fallback
 *
 * Slice 7E: each rendered_paragraph's body_html is parsed via
 * parseNumeralChips so every ``s6-num`` span becomes a smart
 * <NumeralChip> with hover/click event-bus emission, tooltip,
 * and an amber warn state when the numeral is not in the
 * matter's reference-signs registry.
 */
import React, { useEffect, useMemo } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecSectionRow } from '@/api/client';
import { parseNumeralChips } from '@/lib/parseNumeralChips';

interface Props {
  section: SpecSectionRow;
}

export default function AutoGeneratedSection({ section }: Props): JSX.Element {
  const render = useSpecStore((s) => s.renderBySection[section.id] ?? null);
  const status = useSpecStore(
    (s) => s.renderStatusBySection[section.id] ?? 'idle',
  );
  const error = useSpecStore(
    (s) => s.renderErrorBySection[section.id] ?? null,
  );
  const loadSectionRender = useSpecStore((s) => s.loadSectionRender);
  const referenceSigns = useSpecStore((s) => s.referenceSigns);

  const knownNumerals = useMemo(
    () => new Set(referenceSigns.map((r) => r.numeral)),
    [referenceSigns],
  );
  const labelByNumeral = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of referenceSigns) m.set(r.numeral, r.label);
    return m;
  }, [referenceSigns]);

  const source = section.section_kind === 'reference_signs_list'
    ? ('reference_signs_table' as const)
    : ('claims_mirror' as const);

  // Lazy-load on mount if the cache is empty. setActiveSection
  // already triggers this for the user-driven path; this handles
  // the rare case where the component mounts without a fresh
  // setActiveSection call (e.g. direct hydration to an auto
  // section that was already in render cache after a hard reload).
  useEffect(() => {
    if (!render && status === 'idle') {
      void loadSectionRender(section.id);
    }
  }, [render, status, section.id, loadSectionRender]);

  const handleRefresh = (): void => {
    void loadSectionRender(section.id, { force: true });
  };

  const isLoading = status === 'loading';
  const sourceSummary = render?.source_summary as
    | { numeral_count?: number; skipped_orphans?: number; drawing_count?: number;
        claim_count?: number; jurisdiction?: string }
    | undefined;

  const summaryLine = render
    ? section.section_kind === 'reference_signs_list'
      ? `${sourceSummary?.numeral_count ?? 0} numerals` +
        (sourceSummary?.skipped_orphans
          ? ` · ${sourceSummary.skipped_orphans} unused omitted`
          : '')
      : `${sourceSummary?.claim_count ?? 0} claims · ${sourceSummary?.jurisdiction ?? 'EP'}`
    : null;

  return (
    <div className="mt-6" data-testid="spec-auto-section">
      <div
        className="mb-4 flex items-center justify-between rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2"
        data-testid="spec-auto-section-banner"
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
            Auto-generated
          </span>
          {summaryLine && (
            <span className="text-xs text-zinc-600">{summaryLine}</span>
          )}
        </div>
        <button
          type="button"
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          onClick={handleRefresh}
          disabled={isLoading}
          data-testid="spec-auto-section-refresh"
          title="Refetch the latest render"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Refresh
        </button>
      </div>

      {status === 'error' && (
        <div
          className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          data-testid="spec-auto-section-error"
          role="alert"
        >
          {error ?? 'Render failed.'}
          <button
            type="button"
            className="ml-2 underline underline-offset-2 hover:text-rose-900"
            onClick={handleRefresh}
          >
            Retry
          </button>
        </div>
      )}

      {!render && status === 'loading' && (
        <div
          className="rounded-sm border border-zinc-200 bg-white px-4 py-8 text-center text-xs text-zinc-500"
          data-testid="spec-auto-section-loading"
        >
          Loading…
        </div>
      )}

      {render && render.rendered_paragraphs.length === 0 && (
        <div
          className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-3 py-6 text-center text-xs text-zinc-500"
          data-testid="spec-auto-section-empty"
        >
          {section.section_kind === 'reference_signs_list'
            ? 'No reference signs in use yet.'
            : 'No frozen claims yet.'}
        </div>
      )}

      {render && render.rendered_paragraphs.length > 0 && (
        <div className="space-y-3" data-testid="spec-auto-section-body">
          {render.rendered_paragraphs.map((p) => (
            <div
              key={p.paragraph_number}
              className="s6-para s6-para-auto"
              data-paragraph-number={p.paragraph_number}
            >
              {parseNumeralChips(p.body_html, {
                knownNumerals,
                source,
                resolveLabel: (num) => labelByNumeral.get(num) ?? null,
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
